import React, { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { Helmet } from "react-helmet-async";
import useScrollReveal from "../../hooks/useScrollReveal";
import Nav from "../site/Nav";
import SEO from "../site/SEO";
import Footer from "../site/Footer";
import FinalCta from "../site/FinalCta";

const openAudit = () => window.dispatchEvent(new Event("open-audit"));

const WEBSITE_FAQ = [
  {
    q: "Do you do website refreshes?",
    a: "No. We do not take on random patchwork edits or small refresh projects. We build clean, conversion-ready websites from the ground up so the site launches with the right structure, message, and lead capture flow.",
  },
  {
    q: "How fast can a website launch?",
    a: "Starter Websites can typically launch faster than Growth or Premium Websites, depending on content, access, revisions, and response speed.",
  },
  {
    q: "Can I upgrade from a website into a revenue system later?",
    a: "Yes. The Website Division gives you the front-end foundation. If you later need CRM, automation, follow-up, booking, payment, review, or reporting systems, you can upgrade into a Revenue System.",
  },
];

const REVENUE_FAQ = [
  {
    q: "What is a Revenue System?",
    a: "A Revenue System connects the pieces that happen after someone shows interest: lead capture, CRM routing, follow-up, booking, payment, reviews, reactivation, and reporting.",
  },
  {
    q: "Is this different from a website?",
    a: "Yes. A website is the front-end presence. A Revenue System is the backend process that helps turn leads into booked and paid business.",
  },
  {
    q: "Do I need a website first?",
    a: "Not always. Some clients need a website first. Others already have a website and need the lead flow, CRM, follow-up, or booking system fixed.",
  },
];

/** FAQPage JSON-LD schema for Google rich-result eligibility. Combines both
 *  groups so Google sees a single ItemList of Q&A pairs anchored to /faq. */
const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": "https://themodernexit.net/faq#faqpage",
  url: "https://themodernexit.net/faq",
  mainEntity: [...WEBSITE_FAQ, ...REVENUE_FAQ].map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
};

export default function FaqPage() {
  useScrollReveal();

  return (
    <div className="bg-sand-100 text-ink min-h-screen" data-testid="faq-page">
      <SEO page="/faq" />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(FAQ_SCHEMA)}
        </script>
      </Helmet>
      <Nav />
      <main className="pt-24 sm:pt-28 md:pt-32 pb-12">
        <section className="px-5 sm:px-6 md:px-10">
          <div className="max-w-5xl mx-auto">
            <div className="reveal text-center">
              <p className="eyebrow">FAQ</p>
              <h1
                data-kinetic
                data-testid="faq-headline"
                className="font-display mt-5 text-[2.1rem] sm:text-[2.6rem] md:text-5xl lg:text-[3.4rem] leading-[1.05] tracking-[-0.02em] font-semibold text-ink"
              >
                Answers, before you ask.
              </h1>
              <p className="mt-5 text-[15px] md:text-base text-ink-muted max-w-2xl mx-auto leading-relaxed">
                Two divisions, two sets of common questions. If you don't see
                yours, book a free audit and we'll talk.
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 md:py-24">
          <div className="max-w-3xl mx-auto px-5 sm:px-6 md:px-10">
            <FaqGroup
              eyebrow="Modern Exit Websites"
              title="Website FAQ"
              items={WEBSITE_FAQ}
              prefix="website"
            />
          </div>
        </section>

        <section className="py-16 sm:py-20 md:py-24 bg-sand-200/50 border-y border-sand-300">
          <div className="max-w-3xl mx-auto px-5 sm:px-6 md:px-10">
            <FaqGroup
              eyebrow="Modern Exit Revenue Systems"
              title="Revenue Systems FAQ"
              items={REVENUE_FAQ}
              prefix="revenue"
            />
          </div>
        </section>

        <FinalCta
          eyebrow="Still have questions?"
          headline="Book the audit."
          italicTail="We'll talk through it."
          body="Free conversation. We'll review your current setup and tell you whether you need a website, a system, or both."
          primaryLabel="Book a Free Audit"
          secondaryLabel="See All Packages"
          secondaryHref="/packages"
          onPrimary={openAudit}
        />
      </main>
      <Footer />
    </div>
  );
}

function FaqGroup({ eyebrow, title, items, prefix }) {
  return (
    <div>
      <div className="reveal">
        <p className="eyebrow">{eyebrow}</p>
        <h2 className="font-display mt-4 text-2xl sm:text-3xl md:text-4xl leading-tight tracking-[-0.02em] font-semibold text-ink">
          {title}
        </h2>
      </div>
      <div className="mt-8 sm:mt-10 reveal divide-y divide-sand-300 border-y border-sand-300">
        {items.map((item, i) => (
          <FaqItem
            key={item.q}
            item={item}
            idx={i}
            testId={`${prefix}-faq-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

function FaqItem({ item, idx, testId }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        aria-expanded={open}
        data-testid={`${testId}-toggle`}
        className="w-full flex items-start justify-between gap-6 py-5 sm:py-6 text-left"
      >
        <span className="font-display text-[16px] sm:text-lg md:text-xl text-ink leading-snug">
          {item.q}
        </span>
        <span
          className={`mt-1 h-7 w-7 grid place-items-center rounded-full border border-sand-300 text-ink shrink-0 transition-transform ${
            open ? "bg-ink-deep text-sand-100 border-ink-deep rotate-0" : "bg-sand-50"
          }`}
        >
          {open ? <Minus size={14} /> : <Plus size={14} />}
        </span>
      </button>
      <div
        className={`overflow-hidden transition-[max-height,opacity] duration-300 ${
          open ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <p className="pb-6 text-[14.5px] sm:text-[15px] text-ink-muted leading-relaxed max-w-2xl">
          {item.a}
        </p>
      </div>
    </div>
  );
}
